import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'

// Self-hosted fonts (GDPR-compliant — no calls to fonts.googleapis.com).
// Sistema D2 — Fraunces (display serif), Inter (UI sans), JetBrains Mono
// (labels/tags em mono caps).
import '@fontsource/fraunces/300.css'
import '@fontsource/fraunces/400.css'
import '@fontsource/fraunces/500.css'
import '@fontsource/fraunces/400-italic.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/jetbrains-mono/400.css'
import '@fontsource/jetbrains-mono/500.css'

import './index.css'
import App from './App.tsx'

// ── Sentry init ─────────────────────────────────────────
//
// Empty DSN (dev default) = disabled no-op. Vercel sets VITE_SENTRY_DSN
// in production env; Vite injects it at build time, nothing fetched at
// runtime. We run this BEFORE createRoot so Sentry catches errors from
// the very first render.
//
// PII strategy — mirrors the backend:
//   1. `sendDefaultPii: false` — don't auto-attach cookies/URL params.
//   2. `beforeSend` strips chat/memory/vault content from request bodies
//      if Sentry's breadcrumbs captured them.
//   3. Replay integration DISABLED — session replay could inadvertently
//      record user typing chat content. Stack traces + breadcrumbs are
//      enough for beta debugging.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
    // Errors only by default. Flip traces sample rate up via env when
    // we want perf data; Sentry free tier caps at 10k perf events/mo.
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
    sendDefaultPii: false,
    // Vercel exposes the deploy hash; enables regressed-issue detection.
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || undefined,
    // No session replay — would capture user typing chat content.
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Drop noise from browser extensions injecting into the page (MetaMask
    // injects inpage.js into every tab and routinely throws "Failed to
    // connect to MetaMask" when its background script reloads — not our
    // code). Filter by both message and source URL.
    ignoreErrors: [
      /Failed to connect to MetaMask/i,
      /No extension found with id/i,
    ],
    denyUrls: [
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
    beforeSend(event) {
      try {
        // Drop request body (might contain chat / memory / vault content).
        if (event.request) {
          delete event.request.data
          // Strip auth-carrying headers if captured.
          const headers = event.request.headers || {}
          for (const h of Object.keys(headers)) {
            if (['authorization', 'cookie', 'x-api-key'].includes(h.toLowerCase())) {
              headers[h] = '[REDACTED]'
            }
          }
          event.request.headers = headers
        }
        // Strip secret-looking keys from tags + extra. Sentry's ErrorEvent
        // type doesn't expose these via index signature, so we cast to a
        // permissive shape via `unknown` — safer than the direct cast
        // TypeScript 5.x flags as an overlap mistake.
        const bags = event as unknown as Record<string, Record<string, unknown> | undefined>
        for (const bag of ['tags', 'extra']) {
          const data = bags[bag]
          if (data) {
            for (const k of Object.keys(data)) {
              const lk = k.toLowerCase()
              if (['token', 'secret', 'password', 'key', 'dsn', 'authorization'].some(s => lk.includes(s))) {
                data[k] = '[REDACTED]'
              }
            }
          }
        }
        // Drop user PII — keep id for correlation, drop email/IP.
        if (event.user) {
          delete event.user.email
          delete event.user.ip_address
          delete event.user.username
        }
      } catch {
        // Scrub failure = drop event rather than send under-scrubbed.
        return null
      }
      return event
    },
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// PWA service worker — registered only in production so dev mode stays
// uncached and reloads are immediate. The SW handles push notifications
// (briefing done, pre-meeting alert, Postman operational). Offline mode
// is deliberately NOT implemented in v1 — the product needs the network
// for every meaningful interaction (chat stream, vault, briefing, push).
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .catch((err) => console.warn('SW register failed:', err))
  })
}

